import {useState, Suspense} from 'react';
import {Link} from 'react-router-dom';
import styled from 'styled-components';
import {graphql, useFragment} from 'react-relay';
import {Avatar, EMPHASIS} from '@forecast-it/design-system';
import {LabelledButton} from './LabelledButton';
import type {TicketMetaPanel_ticket$key} from './__generated__/TicketMetaPanel_ticket.graphql';
import type {ticketingPerson_person$key} from '../../../graphql/fragments/__generated__/ticketingPerson_person.graphql';
import type {TicketChannel, TicketPriority, TicketStatus} from '../types/ticket';
import {PriorityBadge} from './PriorityBadge';
import {PriorityPicker} from './PriorityPicker';
import {TicketStatusBadge} from './TicketStatusBadge';
import {relativeTime} from '../utils/relativeTime';
import {AssigneePicker} from './AssigneePicker';
import {TicketLabelPicker} from './TicketLabelPicker';
import {useMentionUsers} from '../hooks/useMentionUsers';
import {useSetTicketLabels} from '../hooks/useSetTicketLabels';
import type {UpdateFieldOptions} from '../hooks/useTicketUpdate';
import {useTicketPerms} from '../../auth/hooks/useTicketPerms';
import {SLABadge} from './SLABadge';
import {ticketingPersonFragment, personDisplayName} from '../../../graphql/fragments/ticketingPerson';
import {wrapLongText} from '../../../styles/wrapLongText';
import {commitTicketingMutation} from '../../../graphql/forecastRelay';
import UpdateTicketCustomFieldValueMutation from '../../../../../mutations/ticketing/tickets/updateTicketCustomFieldValueMutation';

const TicketSidebarFragment = graphql`
	fragment TicketMetaPanel_ticket on Ticket {
		id
		status
		priority
		channel
		assigneeId
		assignee {
			...ticketingPerson_person
		}
		requester {
			id
			name
			email
		}
		createdAt
		updatedAt
		closedAt
		labels {
			id
			name
			color
			category {
				name
			}
		}
		parentTicket {
			id
			number
			subject
			status
		}
		customFields {
			edges {
				node {
					__typename
					id
					key
					type
					displayName
					value
					displayValue
					... on TextCustomFieldValue {
						readOnly
					}
					... on IntegerCustomFieldValue {
						readOnly
					}
					... on DecimalCustomFieldValue {
						readOnly
					}
					... on DateCustomFieldValue {
						readOnly
					}
					... on DropdownCustomFieldValue {
						options {
							id
							displayName
						}
					}
				}
			}
		}
		sla {
			breached
			elapsedSeconds
			thresholdSeconds
			paused
		}
	}
`;

interface Props {
	ticket: TicketMetaPanel_ticket$key;
	saving?: boolean;
	onUpdateField?: (field: string, value: unknown, options?: UpdateFieldOptions) => void;
}

const Aside = styled.aside`
	> * + * {
		margin-top: 16px;
	}
`;

const Card = styled.section`
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	padding: 20px;
	box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
`;

const CardHeader = styled.div`
	margin-bottom: 4px;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const CardTitle = styled.span`
	${p => p.theme.sys.font?.subheading?.['1']}
	letter-spacing: 0.15px;
	color: #0f172a;
`;

const EditIcon = styled.svg`
	width: 12px;
	height: 12px;
`;

const MetaList = styled.dl`
	> div + div {
		border-top: 1px solid #f1f5f9;
	}
`;

const Row = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 0;
`;

const RowLabel = styled.dt`
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #64748b;
`;

const RowValue = styled.dd`
	min-width: 0;
	${wrapLongText}
	margin: 0;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #1e293b;
`;

const FieldSelect = styled.select`
	border-radius: 4px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	padding: 2px 8px;
	${p => p.theme.sys.font?.detail?.['1']}
	color: #1e293b;
	&:disabled {
		opacity: 0.5;
	}
`;

const ChannelText = styled.span`
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #1e293b;
`;

const AvatarChip = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	min-width: 0;
	${wrapLongText}
`;

const Dash = styled.span`
	color: #94a3b8;
`;

const ParentLink = styled(Link)`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	border-radius: 9999px;
	background: #faf5ff;
	padding: 2px 8px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	color: #7e22ce;
	text-decoration: none;
	&:hover {
		background: #f3e8ff;
	}
`;

const LabelsEditColumn = styled.div`
	display: flex;
	width: 192px;
	flex-direction: column;
	align-items: flex-end;
	gap: 6px;
`;

const LabelChips = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-end;
	gap: 4px;
`;

const LabelChip = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	border-radius: 9999px;
	background: #f1f5f9;
	padding: 2px 8px;
	${p => p.theme.sys.font?.detail?.['1']}
`;

const LabelDot = styled.span`
	display: inline-block;
	height: 8px;
	width: 8px;
	border-radius: 9999px;
`;

const LoadingText = styled.span`
	${p => p.theme.sys.font?.detail?.['1']}
	color: #94a3b8;
`;

const CustomFieldInput = styled.input<{$highlight: boolean}>`
	width: 128px;
	border-radius: 4px;
	border: 1px solid ${p => (p.$highlight ? '#fbbf24' : '#e2e8f0')};
	background: ${p => (p.$highlight ? '#fffbeb' : '#ffffff')};
	padding: 2px 8px;
	${p => p.theme.sys.font?.detail?.['1']}
	color: #1e293b;
	&:disabled {
		opacity: 0.5;
	}
`;

const CustomFieldSelect = styled.select<{$highlight: boolean}>`
	width: 128px;
	border-radius: 4px;
	border: 1px solid ${p => (p.$highlight ? '#fbbf24' : '#e2e8f0')};
	background: ${p => (p.$highlight ? '#fffbeb' : '#ffffff')};
	padding: 2px 8px;
	${p => p.theme.sys.font?.detail?.['1']}
	color: #1e293b;
	&:disabled {
		opacity: 0.5;
	}
`;

const CustomFieldValue = styled.span<{$empty: boolean}>`
	${wrapLongText}
	color: ${p => (p.$empty ? '#94a3b8' : '#1e293b')};
`;

function MetaRow({label, children}: {label: string; children: React.ReactNode}) {
	return (
		<Row>
			<RowLabel>{label}</RowLabel>
			<RowValue>{children}</RowValue>
		</Row>
	);
}

const STATUS_OPTIONS: {value: TicketStatus; label: string}[] = [
	{value: 'open', label: 'Open'},
	{value: 'in_progress', label: 'In progress'},
	{value: 'waiting_on_customer', label: 'Waiting on customer'},
	{value: 'resolved', label: 'Resolved'},
	{value: 'closed', label: 'Closed'},
];

const CHANNEL_OPTIONS: {value: TicketChannel; label: string}[] = [
	{value: 'manual', label: 'Manual'},
	{value: 'email', label: 'Email'},
	{value: 'phone', label: 'Phone'},
	{value: 'chat', label: 'Chat'},
	{value: 'internal', label: 'Internal'},
];

const CHANNEL_LABELS: Record<TicketChannel, string> = {
	manual: 'Manual',
	email: 'Email',
	phone: 'Phone',
	chat: 'Chat',
	internal: 'Internal',
	api: 'API',
	customer_portal: 'Client Workspace',
};

export function TicketMetaPanel({ticket: ticketRef, saving = false, onUpdateField}: Props) {
	const [editing, setEditing] = useState(false);
	const [customFieldEdits, setCustomFieldEdits] = useState<Record<string, string | null>>({});
	const {users} = useMentionUsers();
	const ticket = useFragment(TicketSidebarFragment, ticketRef);
	const assignee = useFragment<ticketingPerson_person$key>(ticketingPersonFragment, ticket.assignee ?? null);
	const assigneeLabel = ticket.assignee ? personDisplayName(assignee) : ticket.assigneeId ?? 'Unassigned';
	// "Unassigned" is placeholder copy, not a person — an avatar there would render a
	// bogus "U" initial, so the chip falls back to the muted dash treatment instead.
	const hasAssignee = Boolean(ticket.assignee ?? ticket.assigneeId);
	const requesterLabel = ticket.requester?.name ?? ticket.requester?.email ?? null;
	const {setLabels, inFlight: labelSaving} = useSetTicketLabels();
	// Assignee edit is an updateTicket-backed write — gate on TICKETS_MANAGE to mirror the gateway.
	const {canManage} = useTicketPerms();
	const canEditAssignee = canManage;

	function handleCustomFieldChange(key: string, value: string) {
		setCustomFieldEdits(prev => ({...prev, [key]: value || null}));
	}

	function saveCustomField(key: string, value: string | null) {
		commitTicketingMutation(UpdateTicketCustomFieldValueMutation, {
			entityId: ticket.id,
			entityType: 'TICKET',
			key,
			value,
		});
	}

	function handleDone() {
		Object.entries(customFieldEdits).forEach(([key, value]) => {
			saveCustomField(key, value);
		});
		setCustomFieldEdits({});
		setEditing(false);
	}

	function handleAddLabel(labelId: string) {
		const currentIds = ticket.labels.map(l => l.id);
		setLabels(ticket.id, [...currentIds, labelId]);
	}

	function handleRemoveLabel(labelId: string) {
		const currentIds = ticket.labels.map(l => l.id).filter(id => id !== labelId);
		setLabels(ticket.id, currentIds);
	}

	return (
		<Aside>
			<Card>
				<CardHeader>
					<CardTitle>Details</CardTitle>
					{/* The metadata editors are guarded on TICKETS_MANAGE. Hide the Edit toggle
					    entirely for a view-only viewer so the panel reads as read-only. */}
					{canManage && (
						<LabelledButton
							emphasis={EMPHASIS.LOW}
							label={editing ? 'Done' : 'Edit'}
							onPress={() => {
								if (editing) {
									handleDone();
								} else {
									setEditing(true);
								}
							}}
						>
							<EditIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"
								/>
							</EditIcon>
							{editing ? 'Done' : 'Edit'}
						</LabelledButton>
					)}
				</CardHeader>

				<MetaList>
					<MetaRow label="Status">
						{editing ? (
							<FieldSelect
								value={ticket.status}
								disabled={saving}
								aria-label="Status"
								onChange={e => onUpdateField?.('status', e.target.value as TicketStatus)}
							>
								{STATUS_OPTIONS.map(o => (
									<option key={o.value} value={o.value}>
										{o.label}
									</option>
								))}
							</FieldSelect>
						) : (
							<TicketStatusBadge status={ticket.status as TicketStatus} />
						)}
					</MetaRow>

					<MetaRow label="Priority">
						{editing ? (
							<PriorityPicker
								value={ticket.priority as TicketPriority}
								disabled={saving}
								onChange={p => onUpdateField?.('priority', p)}
							/>
						) : (
							<PriorityBadge priority={ticket.priority as TicketPriority} />
						)}
					</MetaRow>

					<MetaRow label="Assignee">
						{editing && canEditAssignee ? (
							<AssigneePicker
								assigneeId={ticket.assigneeId ?? null}
								users={users}
								disabled={saving}
								onChange={(userId, note) =>
									onUpdateField?.('assigneeId', userId, note ? {handoff_note: note} : undefined)
								}
							/>
						) : (
							<AvatarChip>
								{hasAssignee && <Avatar size="s" name={assigneeLabel} />}
								{assigneeLabel}
							</AvatarChip>
						)}
					</MetaRow>

					<MetaRow label="Requester">
						{requesterLabel ? (
							<AvatarChip>
								<Avatar size="s" name={requesterLabel} />
								{requesterLabel}
							</AvatarChip>
						) : (
							<Dash>—</Dash>
						)}
					</MetaRow>

					<MetaRow label="Project">
						<Dash>—</Dash>
					</MetaRow>

					<MetaRow label="Channel">
						{editing ? (
							<FieldSelect
								value={ticket.channel}
								disabled={saving}
								aria-label="Channel"
								onChange={e => onUpdateField?.('channel', e.target.value as TicketChannel)}
							>
								{CHANNEL_OPTIONS.map(o => (
									<option key={o.value} value={o.value}>
										{o.label}
									</option>
								))}
								{!CHANNEL_OPTIONS.some(o => o.value === ticket.channel) && (
									<option value={ticket.channel}>
										{CHANNEL_LABELS[ticket.channel as TicketChannel] ?? ticket.channel}
									</option>
								)}
							</FieldSelect>
						) : (
							<ChannelText>{CHANNEL_LABELS[ticket.channel as TicketChannel] ?? ticket.channel}</ChannelText>
						)}
					</MetaRow>

					<MetaRow label="Created">{relativeTime(ticket.createdAt)}</MetaRow>

					{ticket.parentTicket && (
						<MetaRow label="Parent ticket">
							<ParentLink to={`/tickets/${ticket.parentTicket.id}`}>#{ticket.parentTicket.number}</ParentLink>
						</MetaRow>
					)}

					<MetaRow label="SLA">
						{ticket.sla && (ticket.sla.thresholdSeconds > 0 || ticket.sla.paused) ? (
							<SLABadge ticket={{sla: ticket.sla}} />
						) : (
							<Dash>—</Dash>
						)}
					</MetaRow>

					<MetaRow label="Labels">
						{editing ? (
							<LabelsEditColumn>
								{ticket.labels.length > 0 && (
									<LabelChips>
										{ticket.labels.map(label => (
											<LabelChip key={label.id}>
												<LabelDot style={{backgroundColor: label.color}} />
												{label.name}
												<LabelledButton
													emphasis={EMPHASIS.LOW}
													label={'Remove ' + label.name}
													disabled={labelSaving}
													onPress={() => handleRemoveLabel(label.id)}
												>
													×
												</LabelledButton>
											</LabelChip>
										))}
									</LabelChips>
								)}
								<Suspense fallback={<LoadingText>Loading...</LoadingText>}>
									<TicketLabelPicker
										ticketId={ticket.id}
										currentLabelIds={ticket.labels.map(l => l.id)}
										disabled={labelSaving}
										onAdd={handleAddLabel}
									/>
								</Suspense>
							</LabelsEditColumn>
						) : ticket.labels.length > 0 ? (
							<LabelChips>
								{ticket.labels.map(label => (
									<LabelChip key={label.id}>
										<LabelDot style={{backgroundColor: label.color}} />
										{label.name}
									</LabelChip>
								))}
							</LabelChips>
						) : (
							<Dash>—</Dash>
						)}
					</MetaRow>

					{(ticket.customFields.edges ?? []).map(edge => {
						const node = edge?.node;
						// Skip edges Relay couldn't resolve to a concrete type.
						if (!node || !node.__typename || node.__typename === '%other') return null;
						// Dropdown nodes carry no `readOnly` field → editable; the scalar
						// types (Text/Integer/Decimal/Date) expose `readOnly`.
						const readOnly = 'readOnly' in node ? node.readOnly === true : false;
						const options = 'options' in node ? node.options ?? [] : [];
						const editedValue = customFieldEdits[node.key];
						const inputValue = editedValue ?? node.value ?? '';
						const label = node.displayName;
						const displayText = node.displayValue ?? node.value ?? '—';

						let inputType = 'text';
						if (node.type === 'INTEGER' || node.type === 'DECIMAL') inputType = 'number';
						else if (node.type === 'DATE') inputType = 'date';

						return (
							<MetaRow key={node.id} label={label}>
								{editing && !readOnly ? (
									node.type === 'DROPDOWN' && options.length > 0 ? (
										<CustomFieldSelect
											aria-label={label}
											$highlight={false}
											value={inputValue}
											disabled={saving}
											onChange={e => handleCustomFieldChange(node.key, e.target.value)}
										>
											<option value="">— Select —</option>
											{options.map(opt => {
												// Dropdown value sent is the option id (decoded from its
												// global id `Type:value`), matching CustomFieldValueInput.tsx.
												// Join past the first segment so a value that itself
												// contains ':' (e.g. a time or URL) isn't truncated.
												const optionValue = atob(opt.id).split(':').slice(1).join(':');
												return (
													<option key={opt.id} value={optionValue}>
														{opt.displayName}
													</option>
												);
											})}
										</CustomFieldSelect>
									) : (
										<CustomFieldInput
											type={inputType}
											aria-label={label}
											$highlight={false}
											value={inputValue}
											disabled={saving}
											onChange={e => handleCustomFieldChange(node.key, e.target.value)}
										/>
									)
								) : (
									<CustomFieldValue $empty={!node.value}>{displayText}</CustomFieldValue>
								)}
							</MetaRow>
						);
					})}
				</MetaList>
			</Card>
		</Aside>
	);
}
